import React from 'react';
import { Info, Sliders } from 'lucide-react';

const cn = (...classes: Array<string | false | null | undefined>) => classes.filter(Boolean).join(' ');

type FieldDef = {
  key: string;
  label: string;
  desc: string;
  type: 'number' | 'toggle';
};

const fields: FieldDef[] = [
  { key: 'threshold_green', label: 'Umbral Verde', type: 'number', desc: 'Límite superior para estado óptimo.' },
  { key: 'threshold_amber', label: 'Umbral Ámbar', type: 'number', desc: 'Límite mínimo para estado estable.' },
  { key: 'stale_1_days', label: 'Stale 1', type: 'number', desc: 'Primer umbral de rezago operativo.' },
  { key: 'stale_2_days', label: 'Stale 2', type: 'number', desc: 'Segundo umbral de rezago operativo.' },
  { key: 'stale_3_days', label: 'Stale 3', type: 'number', desc: 'Tercer umbral de rezago operativo.' },
  { key: 'stale_penalty', label: 'Stale Penalty', type: 'number', desc: 'Penalización aplicada por rezago.' },
  { key: 'stale_penalty_enabled', label: 'Penalty Enabled', type: 'toggle', desc: 'Cuando esta activo, cada mes de rezago operativo descuenta puntos al score segun la penalizacion configurada.' },
  { key: 'quarterly_hardening', label: 'Quarterly Hardening', type: 'toggle', desc: 'Cuando esta activo, el sistema puede endurecer gradualmente los umbrales conforme avanza el ano.' },
  { key: 'operational_toggles_enabled', label: 'Operational Toggles', type: 'toggle', desc: 'Activa controles operativos avanzados. Si no existe uso funcional claro, dejarlo oculto o como opcion tecnica solo para administradores.' }
];

const fallbackValue = (key: string) => {
  const defaults: Record<string, number | boolean> = {
    threshold_green: 90,
    threshold_amber: 75,
    stale_1_days: 1,
    stale_2_days: 2,
    stale_3_days: 3,
    stale_penalty: 10,
    stale_penalty_enabled: true,
    quarterly_hardening: false,
    operational_toggles_enabled: true
  };
  return defaults[key] ?? 0;
};

const OperationalControlSettingsTab = ({
  localSettings,
  updateField,
  isAdmin
}: {
  localSettings: Record<string, any>;
  updateField: (key: string, value: any) => void;
  isAdmin: boolean;
}) => {
  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="flex items-center gap-2 border-b border-slate-800 pb-3">
        <Sliders className="text-amber-300" size={18} />
        <h2 className="text-sm font-black uppercase tracking-widest text-white">Configuración del Control Operativo</h2>
      </div>

      <div className="rounded-lg border border-sky-500/20 bg-sky-500/5 p-4 text-xs font-bold text-sky-200/80">
        <div className="flex items-start gap-2">
          <Info size={14} className="mt-0.5 text-sky-300 shrink-0" />
          <p>Estos valores gobiernan el score operativo, semáforos y rezagos del módulo Control Operativo.</p>
        </div>
      </div>

      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {fields.map((field) => (
          <div key={field.key} className="rounded-lg border border-slate-800 bg-slate-950/70 p-4 space-y-3">
            <div>
              <label className="text-[9px] font-black text-white/50 uppercase tracking-widest block">{field.label}</label>
              <p className="text-[8px] text-white/20 mt-1">{field.desc}</p>
            </div>

            {field.type === 'number' ? (
              <input
                type="number"
                value={Number(localSettings[field.key] ?? fallbackValue(field.key))}
                onChange={(e) => updateField(field.key, Number(e.target.value))}
                disabled={!isAdmin}
                className="h-10 w-full rounded-lg border border-slate-800 bg-black px-3 text-sm font-black text-white focus:border-amber-500/50 focus:outline-none disabled:opacity-50"
              />
            ) : (
              <button
                type="button"
                onClick={() => isAdmin && updateField(field.key, !Boolean(localSettings[field.key] ?? fallbackValue(field.key)))}
                disabled={!isAdmin}
                className={cn(
                  'relative h-7 w-14 rounded-full transition-all disabled:opacity-50',
                  Boolean(localSettings[field.key] ?? fallbackValue(field.key)) ? 'bg-emerald-600' : 'bg-slate-800'
                )}
              >
                <span
                  className={cn(
                    'absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all',
                    Boolean(localSettings[field.key] ?? fallbackValue(field.key)) ? 'left-8' : 'left-1'
                  )}
                />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default OperationalControlSettingsTab;
